import { Link, Navigate, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowUpRight, Clock, Linkedin, Facebook, Twitter } from 'lucide-react';
import { Reveal, MaskedLine } from '../components/Reveal';
import { CTASection } from '../components/CTASection';
import { getPost, posts } from '../data/content';
import { usePageMeta } from '../components/ScrollToTop';

export default function InsightPost() {
  const { slug } = useParams();
  const post = getPost(slug);

  usePageMeta(post ? `${post.title} — TheBigHub Insights` : 'Insights — TheBigHub', post ? post.excerpt : '');

  if (!post) return <Navigate to="/insights" replace />;

  const related = posts.filter((p) => p.slug !== slug).slice(0, 2);
  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';
  const shareLinks = [
    {
      label: 'X (Twitter)',
      icon: Twitter,
      url: `https://twitter.com/intent/tweet?text=${encodeURIComponent(post.title)}&url=${encodeURIComponent(shareUrl)}`,
    },
    {
      label: 'Facebook',
      icon: Facebook,
      url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
    },
    {
      label: 'LinkedIn',
      icon: Linkedin,
      url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
    },
  ];

  return (
    <main data-testid={`insight-post-${slug}`} className="bg-paper">
      <article className="mx-auto max-w-3xl px-5 pb-20 pt-32 sm:px-8 sm:pt-40">
        <Reveal>
          <Link
            to="/insights"
            data-testid="post-back-link"
            className="mb-10 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-ink transition-colors hover:text-forest"
          >
            <ArrowLeft size={14} /> All Insights
          </Link>
        </Reveal>
        <Reveal delay={0.05}>
          <div className="mb-6 flex flex-wrap items-center gap-3">
            <span className="rounded-full bg-forest/10 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.16em] text-forest">
              {post.category}
            </span>
            <span className="flex items-center gap-1 text-xs text-ink">
              <Clock size={12} /> {post.readingTime}
            </span>
          </div>
        </Reveal>
        <h1 className="text-balance font-display text-3xl font-bold leading-[1.12] text-ink sm:text-5xl">
          <MaskedLine delay={0.12}>{post.title}</MaskedLine>
        </h1>
        <Reveal delay={0.3}>
          <p className="mt-6 text-xs text-ink">
            By <span className="font-semibold text-ink">{post.author}</span> ·{' '}
            {new Date(post.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
          </p>
        </Reveal>
        <Reveal delay={0.35}>
          <div className="img-frame mt-10 aspect-[16/9] shadow-[0_30px_70px_-30px_rgba(0,59,36,0.4)]">
            <img src={post.image} alt={post.imageAlt} loading="eager" />
          </div>
        </Reveal>
        <Reveal delay={0.4}>
          <div className="prose-tbh mt-12">
            {post.content.map((para, i) => (
              <p key={i}>{para}</p>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-12 flex flex-wrap items-center gap-3 border-t border-ink/10 pt-8" data-testid="post-share">
            <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-ink">Share:</span>
            {shareLinks.map((s) => {
              const Icon = s.icon;
              return (
                <a
                  key={s.label}
                  href={s.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`share-${s.label.toLowerCase().replace(/[^a-z]/g, '-')}`}
                  aria-label={`Share on ${s.label}`}
                  className="flex h-10 w-10 items-center justify-center rounded-full border border-ink/15 text-ink transition-colors hover:border-forest hover:text-forest"
                >
                  <Icon size={16} />
                </a>
              );
            })}
          </div>
        </Reveal>
      </article>

      <section className="bg-cream">
        <div className="mx-auto max-w-7xl px-5 py-16 sm:px-8 sm:py-20">
          <Reveal>
            <h2 className="mb-10 font-display text-2xl font-bold text-ink sm:text-3xl">Keep Reading</h2>
          </Reveal>
          <div className="grid gap-5 sm:grid-cols-2">
            {related.map((p, i) => (
              <Reveal key={p.slug} delay={i * 0.08}>
                <Link
                  to={`/insights/${p.slug}`}
                  data-testid={`related-post-${p.slug}`}
                  className="card-lift group flex h-full items-center gap-5 rounded-2xl border border-ink/10 bg-white p-5"
                >
                  <div className="img-frame h-24 w-32 shrink-0">
                    <img src={p.image} alt={p.imageAlt} loading="lazy" />
                  </div>
                  <div>
                    <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-forest">{p.category}</span>
                    <h3 className="mt-1 font-display text-base font-bold leading-snug text-ink transition-colors group-hover:text-forest">
                      {p.title}
                    </h3>
                  </div>
                  <ArrowUpRight size={18} className="ml-auto shrink-0 text-ink transition-colors group-hover:text-forest" />
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CTASection />
    </main>
  );
}
